/**
 * Lógica de «cerrar turno» en un puesto, separada del componente para poder probarla.
 *
 * El puesto tiene un lector de códigos que se comporta como un teclado: escribe el código y
 * termina con Enter. Por eso el cierre del turno **nunca** puede ejecutarse con el primer
 * gesto: hace falta armar y luego confirmar, y entre las dos cosas tiene que pasar tiempo
 * suficiente para que un doble toque, un toque «rebotado» o el Enter del lector no cuenten
 * como confirmación.
 */

/** Tiempo mínimo entre armar y confirmar: por debajo, es el mismo gesto. */
export const CONFIRM_DELAY_MS = 500;
/** Si no se confirma en este tiempo, el botón se desarma solo. */
export const ARM_TIMEOUT_MS = 4_000;

export interface EndShiftState {
  /** Momento en que se armó, o `null` si no está armado. */
  armedAt: number | null;
}

export type EndShiftAction = 'arm' | 'confirm' | 'ignore' | 'tooSoon';

/**
 * `event.detail` vale 0 en los clics generados por teclado o por una tecnología de
 * asistencia y 1 en un toque real; 2 o más es el segundo clic de un doble clic.
 */
export function endShiftStep(
  state: EndShiftState,
  event: { detail: number; now: number }
): { state: EndShiftState; action: EndShiftAction } {
  const armed = state.armedAt !== null && event.now - state.armedAt <= ARM_TIMEOUT_MS;

  // El segundo clic de un doble clic no arma ni confirma: es el mismo gesto repetido.
  if (event.detail >= 2) return { state: armed ? state : { armedAt: null }, action: 'ignore' };

  if (!armed) return { state: { armedAt: event.now }, action: 'arm' };

  if (event.now - (state.armedAt as number) < CONFIRM_DELAY_MS) {
    // Demasiado pronto para ser una confirmación: se queda armado, pero no cierra nada.
    return { state, action: 'tooSoon' };
  }

  return { state: { armedAt: null }, action: 'confirm' };
}
